import React from 'react';
import PropTypes from 'prop-types';
import { LuFlag, LuX } from 'react-icons/lu';
import { Field, SelectInput } from '../../../components/form/FormControls';
import { SearchField } from '../../ProfilePage/components/ProfileUi';
import useSmoText from '../smoText';

const Chip = ({ children, onRemove, removeLabel }) => (
  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-teal-500/10 tw-py-1 tw-pl-3 tw-pr-1 tw-text-xs tw-font-semibold tw-text-teal-800 dark:tw-bg-teal-400/10 dark:tw-text-teal-200">
    {children}
    <button type="button" onClick={onRemove} aria-label={removeLabel} title={removeLabel} className="tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded-full tw-bg-transparent hover:tw-bg-teal-500/20">
      <LuX aria-hidden="true" className="tw-h-3 tw-w-3" />
    </button>
  </span>
);

Chip.propTypes = { children: PropTypes.node, onRemove: PropTypes.func.isRequired, removeLabel: PropTypes.string.isRequired };

/**
 * Search plus the Durum / Tip / Kör Durumu filters and the "hide finished
 * projects" switch. They filter the workers' project assignments; a worker
 * with no matching assignment drops out of the list. Also used by Bölge
 * Müdürleri Raporları (no "Tamamlanmamış" option there).
 */
const WorkerFilters = ({ workers, incompleteWorkerCount = 0, showIncomplete = true, searchPlaceholder, help, searchTerm, setSearchTerm, statusFilter, setStatusFilter, primerFilter, setPrimerFilter, blindedFilter, setBlindedFilter, hideFinished, setHideFinished }) => {
  const { label } = useSmoText();
  const count = (test) => workers.filter((w) => w.projects.some(test)).length;
  const anyFilter = statusFilter !== 'all' || primerFilter !== 'all' || blindedFilter !== 'all' || hideFinished;
  const statusText = { active: label('filters.active'), inactive: label('filters.inactive'), incomplete: label('filters.incomplete') };

  return (
    <div className="tw-space-y-3">
      <SearchField value={searchTerm} onChange={setSearchTerm} placeholder={searchPlaceholder || label('filters.search')} className="sm:tw-max-w-md" />

      <div className="tw-space-y-3 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-slate-50/70 tw-p-3 sm:tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
        <div className="tw-grid tw-grid-cols-1 tw-gap-3 min-[420px]:tw-grid-cols-2 lg:tw-grid-cols-4">
          <Field label={label('filters.status')} htmlFor="smo-filter-status">
            <SelectInput
              id="smo-filter-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: 'all', label: label('filters.allCount', { count: workers.length }) },
                { value: 'active', label: `${label('filters.active')} (${count((p) => p.status === 'active')})` },
                { value: 'inactive', label: `${label('filters.inactive')} (${count((p) => p.status !== 'active')})` },
                ...(showIncomplete ? [{ value: 'incomplete', label: `${label('filters.incomplete')} (${incompleteWorkerCount})` }] : []),
              ]}
            />
          </Field>
          <Field label={label('filters.type')} htmlFor="smo-filter-type">
            <SelectInput
              id="smo-filter-type"
              value={primerFilter}
              onChange={(e) => setPrimerFilter(e.target.value)}
              options={[
                { value: 'all', label: label('filters.allTypes', { count: workers.length }) },
                { value: 'primer', label: `Primer (${count((p) => p.isPrimer === true)})` },
                { value: 'backup', label: `Backup (${count((p) => p.isPrimer === false)})` },
              ]}
            />
          </Field>
          <Field label={label('filters.blinded')} htmlFor="smo-filter-blinded">
            <SelectInput
              id="smo-filter-blinded"
              value={blindedFilter}
              onChange={(e) => setBlindedFilter(e.target.value)}
              options={[
                { value: 'all', label: label('filters.allCount', { count: workers.length }) },
                { value: 'blinded', label: `Blinded (${count((p) => p.isBlinded === true)})` },
                { value: 'unblinded', label: `Unblinded (${count((p) => p.isBlinded === false)})` },
              ]}
            />
          </Field>
          <Field label={label('filters.finished')} htmlFor="smo-filter-finished">
            <button
              id="smo-filter-finished"
              type="button"
              role="switch"
              aria-checked={hideFinished}
              onClick={() => setHideFinished((v) => !v)}
              title={label('filters.finishedHint')}
              className={`tw-inline-flex tw-h-10 tw-w-full tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-px-3.5 tw-text-sm tw-font-semibold tw-transition-colors ${
                hideFinished
                  ? 'tw-border-violet-500/40 tw-bg-violet-500/10 tw-text-violet-800 dark:tw-border-violet-400/30 dark:tw-text-violet-200'
                  : 'tw-border-slate-200 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-200'
              }`}
            >
              <LuFlag aria-hidden="true" className="tw-h-4 tw-w-4" />
              {hideFinished ? label('filters.finishedHidden') : label('filters.hideFinished')}
            </button>
          </Field>
        </div>

        {/* Active Filters Display (Chips) */}
        {anyFilter && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            {statusFilter !== 'all' && <Chip onRemove={() => setStatusFilter('all')} removeLabel={label('filters.remove')}>{statusText[statusFilter]}</Chip>}
            {primerFilter !== 'all' && <Chip onRemove={() => setPrimerFilter('all')} removeLabel={label('filters.remove')}>{primerFilter === 'primer' ? 'Primer' : 'Backup'}</Chip>}
            {blindedFilter !== 'all' && <Chip onRemove={() => setBlindedFilter('all')} removeLabel={label('filters.remove')}>{blindedFilter === 'blinded' ? 'Blinded' : 'Unblinded'}</Chip>}
            {hideFinished && <Chip onRemove={() => setHideFinished(false)} removeLabel={label('filters.remove')}>{label('filters.finishedHidden')}</Chip>}
            <button
              type="button"
              onClick={() => {
                setStatusFilter('all');
                setPrimerFilter('all');
                setBlindedFilter('all');
                setHideFinished(false);
              }}
              className="tw-rounded-full tw-bg-transparent tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-text-rose-600 hover:tw-bg-rose-500/10 dark:tw-text-rose-300"
            >
              {label('filters.clearAll')}
            </button>
          </div>
        )}
        <p className="tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{help || label('filters.help')}</p>
      </div>
    </div>
  );
};

WorkerFilters.propTypes = {
  workers: PropTypes.array.isRequired,
  incompleteWorkerCount: PropTypes.number,
  showIncomplete: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  help: PropTypes.string,
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  statusFilter: PropTypes.string.isRequired,
  setStatusFilter: PropTypes.func.isRequired,
  primerFilter: PropTypes.string.isRequired,
  setPrimerFilter: PropTypes.func.isRequired,
  blindedFilter: PropTypes.string.isRequired,
  setBlindedFilter: PropTypes.func.isRequired,
  hideFinished: PropTypes.bool.isRequired,
  setHideFinished: PropTypes.func.isRequired,
};

export default WorkerFilters;
